import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, Table, TableBody, TableCell, 
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, IconButton, Chip, Drawer, Grid, Card, CardContent, Divider,
  Avatar, List, ListItem, ListItemText, ListItemIcon, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, InputAdornment, Tooltip, CircularProgress, TablePagination, Stack
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

function Clients() {
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', crmID: '', contact_info: '', email: '', phone: '', country_id: '' });
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterLetter, setFilterLetter] = useState('All');
  const [crmLoading, setCrmLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [files, setFiles] = useState({
      license_1: null,
      license_2: null,
      tax_card: null,
      establishment_card: null,
      application_pdf: null
  });
  const userRole = localStorage.getItem('user_role');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClients = async () => {
    // setLoading(true); // Removed to prevent re-rendering the whole page on search
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCountry) params.country_id = filterCountry;

      const resClients = await axios.get(`${API_URL}/clients`, { headers, params });
      setClients(resClients.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (error) {
            console.error(error);
        }
    }
    getCountries();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchClients();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [search, filterCountry]);

  const handleFileChange = (e) => {
      setFiles({ ...files, [e.target.name]: e.target.files[0] });
  };

  const handleFetchFromCRM = async () => {
        if (!newClient.crmID) {
            alert("Please enter a CRM ID first.");
            return;
        }
        setCrmLoading(true);
        try {
            const res = await axios.get(`${API_URL}/clients/fetch-crm`, {
                headers,
                params: { crm_id: newClient.crmID }
            });
            const data = res.data;
            if (data.found) {
                setNewClient(prev => {
                    let newCountryId = prev.country_id;
                    if (data.country) {
                        const countryMatch = countries.find(c => c.name.toLowerCase() === data.country.toLowerCase());
                        if (countryMatch) {
                            newCountryId = countryMatch.id;
                        }
                    }
                    
                    return {
                        ...prev,
                        name: data.name || prev.name,
                        email: data.email || prev.email,
                        phone: data.phone || prev.phone,
                        contact_info: data.contact_info || prev.contact_info,
                        country_id: newCountryId
                    };
                });
                alert("Client data fetched from CRM successfully!");
            }
        } catch (err) {
            console.error(err);
            if (err.response && err.response.data && err.response.data.message) {
                alert(`Error: ${err.response.data.message}`);
            } else {
                alert("Failed to fetch from CRM. Please check the ID and try again.");
            }
        } finally {
            setCrmLoading(false);
        }
    };

  const handleCreate = async () => {
    setCreateLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newClient.name);
      formData.append('crmID', newClient.crmID);
      formData.append('contact_info', newClient.contact_info);
      formData.append('email', newClient.email);
      formData.append('phone', newClient.phone);
      formData.append('country_id', newClient.country_id || '');
      
      if (files.license_1) formData.append('license_1', files.license_1);
      if (files.license_2) formData.append('license_2', files.license_2);
      if (files.tax_card) formData.append('tax_card', files.tax_card);
      if (files.establishment_card) formData.append('establishment_card', files.establishment_card);
      if (files.application_pdf) formData.append('application_pdf', files.application_pdf);

      await axios.post(`${API_URL}/clients`, formData, { 
          headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
      });
      setOpenAdd(false);
      fetchClients();
      setNewClient({ name: '', crmID: '', contact_info: '', email: '', phone: '', country_id: '' });
      setFiles({ license_1: null, license_2: null, tax_card: null, establishment_card: null, application_pdf: null });
    } catch (err) {
      alert('Error creating client');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleViewDetails = async (client) => {
      setSelectedClient(client);
      setOpenDetails(true);
      setEditMode(false);
      try {
          const res = await axios.get(`${API_URL}/clients/${client.id}`, { headers });
          setClientDetails(res.data);
      } catch (err) {
          console.error(err);
      }
  };

  const handleDeleteClient = async () => {
      if(!window.confirm("Are you sure you want to delete this client?")) return;
      try {
          await axios.delete(`${API_URL}/clients/${clientDetails.id}`, { headers: { Authorization: `Bearer ${token}` } });
          setOpenDetails(false);
          fetchClients();
          alert("Client deleted successfully");
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.message || "Error deleting client");
      }
  };

  const handleUpdateClient = async () => {
      setUpdateLoading(true);
      try {
          const payload = { ...clientDetails, country_id: clientDetails.country_id || null };
          await axios.put(`${API_URL}/clients/${clientDetails.id}`, payload, { headers });
          setEditMode(false);
          fetchClients(); // Refresh list
      } catch (err) {
          alert('Error updating client');
      } finally {
          setUpdateLoading(false);
      }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredClients = clients.filter(c => {
    if (filterLetter !== 'All') {
      return c.name && c.name.toUpperCase().startsWith(filterLetter);
    }
    return true;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (loading) return <Loading message="Loading Clients..." />;

  const letters = ['All', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
           <Typography variant="h6" fontWeight="bold">Clients</Typography>
           <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setOpenAdd(true)}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Add Client
           </Button>
        </Box>
        
        <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 0.5, 
            justifyContent: 'center',
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 2
        }}>
            {letters.map(letter => (
                <Box
                    key={letter}
                    onClick={() => setFilterLetter(letter)}
                    sx={{
                        cursor: 'pointer',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        fontWeight: filterLetter === letter ? 'bold' : 'normal',
                        bgcolor: filterLetter === letter ? 'primary.main' : 'transparent',
                        color: filterLetter === letter ? 'white' : 'text.primary',
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: filterLetter === letter ? 'primary.dark' : 'action.selected',
                        }
                    }}
                >
                    <Typography variant="body2">{letter}</Typography>
                </Box>
            ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' } }}>
             <TextField 
                size="small" 
                placeholder="Search by name, email, or phone..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                }}
                sx={{ flexGrow: 1 }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon color="action" />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Country</InputLabel>
                    <Select
                        value={filterCountry}
                        label="Filter by Country"
                        onChange={(e) => setFilterCountry(e.target.value)}
                    >
                        <MenuItem value=""><em>All Countries</em></MenuItem>
                        {countries.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
      </Paper>
      
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Table sx={{ minWidth: 1200, width: '100%' }}>
            <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>CRM ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
            {filteredClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(c => (
                <TableRow key={c.id} hover>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>{c.name[0]}</Avatar>
                            <Typography fontWeight="medium">{c.name}</Typography>
                        </Box>
                    </TableCell>
                    <TableCell><Chip label={c.crmID || 'N/A'} size="small" variant="outlined" /></TableCell>
                    <TableCell>{c.email || '-'}</TableCell>
                    <TableCell>{c.phone || '-'}</TableCell>
                    <TableCell>{c.country || '-'}</TableCell>
                    <TableCell>
                        {c.status === 'Approved' ? 
                            <Chip icon={<CheckCircleIcon />} label="Approved" color="success" size="small" variant="outlined" /> : 
                         c.status === 'Rejected' ?
                            <Chip icon={<CancelIcon />} label="Rejected" color="error" size="small" variant="outlined" /> :
                            <Chip icon={<AssignmentIcon />} label="Pending" color="warning" size="small" variant="outlined" />
                        }
                    </TableCell>
                    <TableCell align="right">
                        <IconButton size="small" onClick={() => handleViewDetails(c)} color="primary">
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </TableCell>
                </TableRow>
            ))}
            {filteredClients.length === 0 && (
                <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No clients found. Add one to get started.
                    </TableCell>
                </TableRow>
            )}
            </TableBody>
        </Table>
        </Box>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredClients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add Client Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Client</DialogTitle>
        <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="Client Name" fullWidth value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} />
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                    <TextField 
                        label="CRM ID" 
                        fullWidth 
                        value={newClient.crmID} 
                        onChange={e => setNewClient({...newClient, crmID: e.target.value})} 
                    />
                    <Tooltip title="Fetch Data from CRM">
                        <span>
                            <Button 
                                variant="contained" 
                                color="secondary" 
                                onClick={handleFetchFromCRM} 
                                disabled={crmLoading || !newClient.crmID}
                                sx={{ height: { xs: 40, sm: 56 }, width: { xs: '100%', sm: 'auto' }, minWidth: 56, px: 3, whiteSpace: 'nowrap' }}
                            >
                                {crmLoading ? <CircularProgress size={24} color="inherit" /> : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CloudDownloadIcon /> Get from CRM</Box>}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField label="Email" fullWidth value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Phone" fullWidth value={newClient.phone} onChange={e => setNewClient({...newClient, phone: e.target.value})} />
                    </Grid>
                </Grid>
                <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                        value={newClient.country_id}
                        label="Country"
                        onChange={e => setNewClient({...newClient, country_id: e.target.value})}
                    >
                        {countries.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField label="Additional Contact Info" fullWidth multiline rows={2} value={newClient.contact_info} onChange={e => setNewClient({...newClient, contact_info: e.target.value})} />
                
                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>Required Documents</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            License 1 (Img)
                            <input type="file" hidden name="license_1" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.license_1 && <Typography variant="caption" display="block" noWrap>{files.license_1.name}</Typography>}
                    </Grid>
                    <Grid item xs={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            License 2 (Img)
                            <input type="file" hidden name="license_2" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.license_2 && <Typography variant="caption" display="block" noWrap>{files.license_2.name}</Typography>}
                    </Grid>
                    <Grid item xs={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            Tax Card (Img)
                            <input type="file" hidden name="tax_card" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.tax_card && <Typography variant="caption" display="block" noWrap>{files.tax_card.name}</Typography>}
                    </Grid>
                    <Grid item xs={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            Est. Card (Img)
                            <input type="file" hidden name="establishment_card" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.establishment_card && <Typography variant="caption" display="block" noWrap>{files.establishment_card.name}</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            Application (PDF)
                            <input type="file" hidden name="application_pdf" accept="application/pdf" onChange={handleFileChange} />
                        </Button>
                        {files.application_pdf && <Typography variant="caption" display="block" noWrap>{files.application_pdf.name}</Typography>}
                    </Grid>
                </Grid>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create Client'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Client Details Drawer */}
      <Drawer
        anchor="right"
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 500 }, p: 0 } }}
      >
          {clientDetails ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Header */}
                  <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ width: 56, height: 56, bgcolor: 'white', color: 'primary.main', fontSize: '1.5rem' }}>
                                  {clientDetails.name[0]}
                              </Avatar>
                              <Box>
                                  <Typography variant="h5" fontWeight="bold">{clientDetails.name}</Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.8 }}>ID: {clientDetails.crmID}</Typography>
                              </Box>
                          </Box>
                          <IconButton onClick={() => setOpenDetails(false)} sx={{ color: 'white' }}>
                              <CloseIcon />
                          </IconButton>
                      </Box>
                  </Box>

                  <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                      {/* Info Section */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" fontWeight="bold">Client Information</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {['Admin', 'Manager'].includes(userRole) && clientDetails.projects && clientDetails.projects.length === 0 && (
                                <IconButton onClick={handleDeleteClient} color="error" title="Delete Client" size="small">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            <Button 
                                startIcon={editMode ? <CloseIcon /> : <EditIcon />} 
                                onClick={() => setEditMode(!editMode)}
                                size="small"
                            >
                                {editMode ? 'Cancel' : 'Edit'}
                            </Button>
                          </Box>
                      </Box>

                      {editMode ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                              <TextField label="Name" fullWidth value={clientDetails.name} onChange={e => setClientDetails({...clientDetails, name: e.target.value})} />
                              <TextField label="CRM ID" fullWidth value={clientDetails.crmID} onChange={e => setClientDetails({...clientDetails, crmID: e.target.value})} />
                              <TextField label="Email" fullWidth value={clientDetails.email} onChange={e => setClientDetails({...clientDetails, email: e.target.value})} />
                              <TextField label="Phone" fullWidth value={clientDetails.phone} onChange={e => setClientDetails({...clientDetails, phone: e.target.value})} />
                              <FormControl fullWidth>
                                  <InputLabel>Country</InputLabel>
                                  <Select
                                      value={clientDetails.country_id || ''}
                                      label="Country"
                                      onChange={e => setClientDetails({...clientDetails, country_id: e.target.value})}
                                  >
                                      {countries.map((c) => (
                                          <MenuItem key={c.id} value={c.id}>
                                              {c.name}
                                          </MenuItem>
                                      ))}
                                  </Select>
                              </FormControl>
                              {['Admin', 'Manager', 'Country Manager', 'Division Manager'].includes(userRole) && (
                                <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={clientDetails.status || (clientDetails.approved ? 'Approved' : 'Pending')}
                                            label="Status"
                                            onChange={(e) => setClientDetails({...clientDetails, status: e.target.value})}
                                        >
                                            <MenuItem value="Pending">Pending</MenuItem>
                                            <MenuItem value="Approved">Approved</MenuItem>
                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                    
                                    {clientDetails.status === 'Rejected' && (
                                        <TextField
                                            label="Rejection Reason"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={clientDetails.rejection_comment || ''}
                                            onChange={(e) => setClientDetails({...clientDetails, rejection_comment: e.target.value})}
                                            sx={{ mt: 2 }}
                                        />
                                    )}
                                </Box>
                              )}
                              <Button variant="contained" onClick={handleUpdateClient} disabled={updateLoading}>
                                  {updateLoading ? 'Saving...' : 'Save Changes'}
                              </Button>
                          </Box>
                      ) : (
                          <Grid container spacing={2} sx={{ mb: 4 }}>
                              <Grid item xs={12}>
                                  <List dense>
                                      <ListItem>
                                          <ListItemIcon>
                                              {clientDetails.status === 'Approved' ? <CheckCircleIcon color="success" /> : 
                                               clientDetails.status === 'Rejected' ? <CancelIcon color="error" /> : 
                                               <AssignmentIcon color="warning" />}
                                          </ListItemIcon>
                                          <ListItemText 
                                              primary="Status" 
                                              secondary={clientDetails.status || (clientDetails.approved ? "Approved" : "Pending")} 
                                          />
                                      </ListItem>
                                      {clientDetails.status === 'Rejected' && (
                                          <ListItem>
                                              <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                              <ListItemText 
                                                  primary="Rejection Reason" 
                                                  secondary={clientDetails.rejection_comment || 'No reason provided'} 
                                              />
                                          </ListItem>
                                      )}
                                      <ListItem>
                                          <ListItemIcon><EmailIcon color="action" /></ListItemIcon>
                                          <ListItemText primary="Email" secondary={clientDetails.email || 'Not provided'} />
                                      </ListItem>
                                      <ListItem>
                                          <ListItemIcon><PhoneIcon color="action" /></ListItemIcon>
                                          <ListItemText primary="Phone" secondary={clientDetails.phone || 'Not provided'} />
                                      </ListItem>
                                      <ListItem>
                                          <ListItemIcon><PublicIcon color="action" /></ListItemIcon>
                                          <ListItemText primary="Country" secondary={clientDetails.country || 'Not provided'} />
                                      </ListItem>
                                      <ListItem>
                                          <ListItemIcon><BusinessIcon color="action" /></ListItemIcon>
                                          <ListItemText primary="Contact Info" secondary={clientDetails.contact_info || 'Not provided'} />
                                      </ListItem>
                                  </List>

                                  {clientDetails.files && (
                                    <>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>Documents</Typography>
                                        <List dense>
                                            {Object.entries(clientDetails.files).map(([key, path]) => {
                                                if (!path) return null;
                                                const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                                return (
                                                    <ListItem key={key}>
                                                        <ListItemIcon><AssignmentIcon color="primary" /></ListItemIcon>
                                                        <ListItemText 
                                                            primary={label} 
                                                            secondary={
                                                                <a href={`${API_URL}/clients/${clientDetails.id}/files/${path}?token=${token}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                                                                    Download / View
                                                                </a>
                                                            } 
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </>
                                  )}
                              </Grid>
                          </Grid>
                      )}

                      <Divider sx={{ my: 2 }} />

                      {/* Projects Section */}
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Projects ({clientDetails.projects.length})</Typography>
                      
                      {clientDetails.projects.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {clientDetails.projects.map(proj => (
                                  <Card key={proj.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                      <CardContent sx={{ pb: 2 }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                              <Typography variant="subtitle1" fontWeight="bold">{proj.name}</Typography>
                                              <Chip 
                                                label={proj.status} 
                                                size="small" 
                                                color={proj.status === 'In Progress' ? 'primary' : 'default'} 
                                              />
                                          </Box>
                                          <Typography variant="caption" color="textSecondary" display="block">
                                              Ref: {proj.ref}
                                          </Typography>
                                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Box sx={{ flexGrow: 1, height: 6, bgcolor: 'grey.100', borderRadius: 1 }}>
                                                  <Box sx={{ width: `${proj.progress}%`, height: '100%', bgcolor: 'success.main', borderRadius: 1 }} />
                                              </Box>
                                              <Typography variant="caption">{proj.progress}%</Typography>
                                          </Box>
                                      </CardContent>
                                  </Card>
                              ))}
                          </Box>
                      ) : (
                          <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                              <AssignmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                              <Typography color="textSecondary">No projects for this client yet.</Typography>
                          </Box>
                      )}
                  </Box>
              </Box>
          ) : (
              <Loading message="Loading details..." />
          )}
      </Drawer>
    </Box>
  );
}

export default Clients;
